// New simplified authentication system using API-based auth
// This replaces the complex Firebase SDK + proxy system

import { stwFetch } from '@/utils/stwFetch';

// IMPORTANT: API_URL already includes the /v1 prefix (e.g., http://localhost:8080/v1)
// Do NOT add /v1 to routes - this will cause double prefix errors (/v1/v1/...)
const API_URL = import.meta.env.VITE_API_URL;

// Token storage keys
const ACCESS_TOKEN_KEY = '__STW_ACCESS_TOKEN__';
const REFRESH_TOKEN_KEY = '__STW_REFRESH_TOKEN__';

// Token management
export const setTokens = (accessToken: string, refreshToken: string) => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const clearTokens = () => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Auth API calls
interface AuthResponse {
  access_token: string;
  refresh_token: string;
  user: {
    id: string;
    email_address: string;
    username: string;
  };
}

interface RefreshResponse {
  access_token: string;
  refresh_token: string;
}

/**
 * Login with email and password
 */
export const login = async (
  email: string,
  password: string
): Promise<{
  success: boolean;
  user: any;
  method: 'api';
}> => {
  try {
    const response = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.toLowerCase(), password }),
    });

    if (!response.ok) {
      const { error } = await response.json();
      throw new Error(error || 'Login failed');
    }

    const { data }: { data: AuthResponse } = await response.json();

    // Store tokens
    setTokens(data.access_token, data.refresh_token);

    return {
      success: true,
      user: data.user,
      method: 'api',
    };
  } catch (error: any) {
    throw new Error(error.message || 'Login failed');
  }
};

/**
 * Register new user account
 */
export const register = async (
  email: string,
  password: string,
  username: string,
  turnstileResponse?: string
): Promise<{
  success: boolean;
  method: 'api';
}> => {
  try {
    const response = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: email.toLowerCase(),
        password,
        username,
        turnstile_response: turnstileResponse,
      }),
    });

    if (!response.ok) {
      const { error } = await response.json();
      throw new Error(error || 'Registration failed');
    }

    const { data }: { data: { success: boolean; message: string } } =
      await response.json();

    return {
      success: data.success,
      method: 'api',
    };
  } catch (error: any) {
    throw new Error(error.message || 'Registration failed');
  }
};

/**
 * Refresh access token using refresh token
 */
export const refreshAccessToken = async (): Promise<boolean> => {
  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        refresh_token: refreshToken,
      }),
    });

    if (!response.ok) {
      throw new Error('Token refresh failed');
    }

    const { data }: { data: RefreshResponse } = await response.json();

    // Update stored tokens
    setTokens(data.access_token, data.refresh_token);

    return true;
  } catch (error: any) {
    // Clear tokens if refresh fails
    clearTokens();
    return false;
  }
};

/**
 * Send password reset email
 */
export const resetPassword = async (
  email: string
): Promise<{
  success: boolean;
  method: 'api';
}> => {
  try {
    const response = await fetch(`${API_URL}/auth/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email: email.toLowerCase() }),
    });

    if (!response.ok) {
      const { error } = await response.json();
      throw new Error(error || 'Password reset failed');
    }

    return {
      success: true,
      method: 'api',
    };
  } catch (error: any) {
    throw new Error(error.message || 'Password reset failed');
  }
};

/**
 * Change password for logged-in user
 */
export const changePassword = async (
  currentPassword: string,
  newPassword: string
): Promise<{
  success: boolean;
  method: 'api';
}> => {
  try {
    const accessToken = getAccessToken();
    if (!accessToken) {
      throw new Error('No access token found. Please log in again.');
    }

    const response = await fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        current_password: currentPassword,
        new_password: newPassword,
      }),
    });

    if (!response.ok) {
      const { error } = await response.json();
      throw new Error(error || 'Password change failed');
    }

    return {
      success: true,
      method: 'api',
    };
  } catch (error: any) {
    throw new Error(error.message || 'Password change failed');
  }
};

/**
 * Connect Shopify shop to current user account
 */
export const connectShopify = async (shopId: string): Promise<boolean> => {
  await stwFetch('POST', `/auth/connect/shopify/${shopId}`);
  return true;
};

/**
 * Logout user and clear tokens
 */
export const logout = async () => {
  clearTokens();

  // Clear user from auth store
  const { useAuthStore } = await import('@/stores/auth');
  const authStore = useAuthStore();
  await authStore.setUser();

  return { success: true };
};

/**
 * Get current access token (with automatic refresh)
 */
export const getCurrentToken = async (): Promise<string | null> => {
  const accessToken = getAccessToken();
  if (!accessToken) {
    return null;
  }

  // Check if token is expired by trying to decode it
  try {
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    const now = Math.floor(Date.now() / 1000);

    // If token expires in less than 5 minutes, refresh it
    if (payload.exp && payload.exp - now < 300) {
      const refreshSuccessful = await refreshAccessToken();
      if (refreshSuccessful) {
        return getAccessToken();
      } else {
        return null;
      }
    }

    return accessToken;
  } catch (error) {
    // If token is malformed, try to refresh
    const refreshSuccessful = await refreshAccessToken();
    return refreshSuccessful ? getAccessToken() : null;
  }
};

/**
 * Check if user has valid tokens
 */
export const hasValidTokens = (): boolean => {
  return !!(getAccessToken() && getRefreshToken());
};

/**
 * Check if user has valid proxy token (legacy compatibility)
 */
export const hasValidProxyToken = (): boolean => {
  return hasValidTokens();
};

/**
 * Initialize auth system (simplified - no longer needed)
 */
export const initAuth = async (): Promise<boolean> => {
  // No initialization needed for API-based auth
  return true;
};

// Legacy compatibility exports (undefined since we don't use Firebase anymore)
export let auth: undefined;
export let storage: undefined;
export let provider: undefined;
